import Vue from 'vue';
  import Vuex from 'vuex';
import Swagger from "swagger-client";
import ApiErrorHandler from '@/utils/ApiErrorHandler';
import i18n from "@utils/i18n";

Vue.use(Vuex);

const state = {
  studyId: null,
  study: null,
  arm: null,
  useLocales: false,
  locales: [],
  account: null,
  currentPage: null,
  surveyLinks: [],
  loading: false,
  showHeader: true,
  navigationPages: [],
  notifications: [],
  dashboardNotifications: [],
  dashboardSurveys: [],
  notificationPage: 1,
  notificationTotalPages: 1,
  showNotifications: false,
  showNavigation: false,
  hideNavigationButton: true,
  loadingNotifications: false,
  swaggerUrl: null,
  client: null,
  pollTimer: null,
};

const getters = {
  getPortalStudyName: state => {
    return state.study.portal_name;
  },
  getStudyEmail: state => {
    return state.study.email;
  },
  getStudyPhoneNumber: state => {
    return state.study.phone_number;
  },
  isLoginEnabled: state => {
    return state.study.configuration.portal.loginEnabled;
  },
  isLoggedIn: state => {
    return state.account;
  },
  isStudyEnrollmentDisabled: state => {
    return !state.study.configuration.enrollment.accountCreation.enabled || state.study.status == 'Finished';
  },
  getParticipant: state => {
    if (state.account && state.account.Participants) {
      const su = state.account.Participants.find((su) => {
        return su.study_id == state.studyId;
      });
      return su || false;
    }
    return false;
  },
  isStartedParticipant: (state, getters) => {
    return getters.getParticipant && getters.getParticipant.status === 'Started';
  },
  isArmWithPortal: (state) => {
    if (state.arm && state.arm.enable_portal) {
      return true;
    } else {
      return false;
    }
  },
  authorizableDevices: (state, getters) => {
    if (getters.getParticipant) {
      return getters.getParticipant.participantdatasources.filter(sus => sus.is_authorizable);
    }
    return [];
  }
}

const mutations = {
  SET_OPTIONS: (state, options) => {
    state.swaggerUrl = options.swaggerUrl;
    state.studyId = options.studyId;
    state.study = options.initialStudy;
    state.arm = options.initialArm;
    state.account = options.initialAccount;
    state.participantId = options.initialParticipantId;
    state.useLocales = options.useLocales;
    state.locales = options.locales;
  },
  SET_CLIENT: (state, client) => {
    state.apiClient = client;
    Vue.prototype.$api = client;
  },
  SET_LOADING: (state, loading) => {
    state.loading = loading;
  },
  SET_SHOW_HEADER: (state, show) => {
    state.showHeader = show;
  },
  SET_STUDY: (state, study) => {
    state.study = study;
  },
  SET_ARM: (state, arm) => {
    state.arm = arm;
  },
  SET_ACCOUNT: (state, account) => {
    state.account = account;
    if (window && window.Sentry) {
      window.Sentry.configureScope(function(scope) {
        if (!account) {
          scope.setUser(null);
        } else {
          scope.setUser({
            username: account.username,
            id: account.id
          });
        }
      });
    }
  },
  SET_CURRENT_PAGE: (state, currentPage) => {
    state.currentPage = currentPage;
  },
  SET_LINKED_SURVEYS: (state, surveyLinks) => {
    state.surveyLinks = surveyLinks;
  },
  SET_NOTIFICATIONS: (state, {notifications = [], append = false}) => {
    if (append) {
      state.notifications = state.notifications.concat(notifications);
    } else {
      state.notifications = notifications;
    }
  },
  SET_DASHBOARD_NOTIFICATIONS: (state, dashboardNotifications) => {
    state.dashboardNotifications = dashboardNotifications;
  },
  SET_DASHBOARD_SURVEYS: (state, dashboardSurveys) => {
    state.dashboardSurveys = dashboardSurveys;
  },
  REMOVE_DASHBOARD_NOTIFICATION: (state, notification) => {
    let idx = state.dashboardNotifications.findIndex(n => n.id == notification.id);
    state.dashboardNotifications.splice(idx, 1);
  },
  SET_NOTIFICATION_PAGE: (state, notificationPage) => {
    state.notificationPage = notificationPage;
  },
  SET_TOTAL_NOTIFICATIONS: (state, totalPages) => {
    state.notificationTotalPages = totalPages;
  },
  TOGGLE_NOTIFICATIONS: (state) => {
    state.showNavigation = false;
    state.showNotifications = !state.showNotifications;
  },
  SET_HIDE_NAVIGATION_BUTTON: (state, hide) => {
    state.hideNavigationButton = hide;
  },
  LOADING_NOTIFICATIONS: (state, loading) => {
    state.loadingNotifications = loading;
  },
  SET_NAVIGATION_PAGES: (state, pages = []) => {
    state.navigationPages = pages;
  },
  TOGGLE_NAVIGATION: (state) => {
    state.showNotifications = false;
    state.showNavigation = !state.showNavigation;
  },
  CLOSE_PANELS: (state) => {
    state.showNotifications = false;
    state.showNavigation = false;
    state.notificationPage = 1;
  },
  POLL_TIMER: (state, payload) => {
    state.pollTimer = payload;
  }
};


const actions = {
  API_INIT: async ({state, commit}) => {
    if (state.apiClient) {
      return Promise.resolve();
    }

    const client = await Swagger({
      url: state.swaggerUrl,
      requestInterceptor: (req) => {
        req.headers['Accept-Language'] = i18n.locale;
        req.headers['Enrollment-Channel'] = 'ParticipantPortal';
        if (window && window.localStorage && window.localStorage.getItem('Enrollment-Extra')) {
          req.headers['Enrollment-Extra'] = window.localStorage.getItem('Enrollment-Extra');
        }
        return req;
      }
    });

    commit('SET_CLIENT', client.apis);
  },
  LOGIN: async ({state, commit, dispatch}, credentials) => {
    const response = await state.apiClient.Authentication.sfLogin({
      ...credentials,
      'type' : 'participant',
      'study_id' : state.studyId,
      'include': 'Participants'
    });
    commit('SET_ACCOUNT', response.body.data);
    await dispatch('LOAD_ARM');
  },
  RESET_REQUEST: async ({state, commit, dispatch}, params) => {
    return await state.apiClient.Authentication.requestPasswordReset({
      content: params,
    });
  },
  VALIDATE_RESET_CODE: async ({state, commit, dispatch}, code) => {
    // This API method returns either 204 or 404. So try/catch becomes true/false
    try {
      await state.apiClient.Authentication.validatePasswordResetCode({
        code,
      });
      return true;
    } catch (e) {
      return false;
    }
  },
  COMPLETE_PASSWORD_RESET: async ({state, commit, dispatch}, {code, password}) => {
    const response = await state.apiClient.Authentication.completePasswordReset({
      code,
      content: {
        password
      }
    });
    commit('SET_ACCOUNT', response.body.data);
  },
  LOGOUT: async ({state, commit}) => {
    try {
      await state.apiClient.Authentication.sfLogout();
      commit('SET_ACCOUNT', false);
      commit('SET_ARM', false);
    } catch (err) {
      // nothing to do if logout fails
    }
  },
  LOAD_STUDY: async ({state, commit}) => {
    if (!state.study) {
      const response = await state.apiClient.Study.getStudy({
        'study_id': state.studyId
      });
      commit('SET_STUDY', response.body.data);
    }
  },
  LOAD_ARM: async ({state, commit, getters}) => {
    if (!state.arm && getters.getParticipant && getters.getParticipant.arm_id) {
      const response = await state.apiClient.Arms.getArm({
        'arm_id': getters.getParticipant.arm_id
      });
      commit('SET_ARM', response.body.data);
    }
  },
  LOAD_ACCOUNT: async ({state, commit}, force = false) => {
    // only update our account from the api if we don't have anything yet or we are forced to
    if (state.account === null || force) {
      // check the api to see if we have an account
      try {
        const response = await state.apiClient.Authentication.me({
          'include': 'Participants,Participants.participantdatasources'
        });
        if (!response.body.data) {
          commit('SET_ACCOUNT', false);
          return;
        }

        // we are logged in, set our account
        commit('SET_ACCOUNT', response.body.data);
        // this.setParticipantId();
      } catch (err) {
        commit('SET_ACCOUNT', false);
      }
    }
  },
  SET_LOCALE: ({state, getters}) => {
    // set the locale to the ppt's locale if logged in as ppt
    if (state.useLocales && getters.getParticipant && i18n.locale !== getters.getParticipant.locale) {
      i18n.locale = getters.getParticipant.locale;
      // change locale in gtag calls
      if (Vue.$gtag) {
        Vue.$gtag.config({
          language: i18n.locale,
          send_page_view: false // don't call a pageview with this change
        });
      }
      // TODO: set vee-validate locale
      // set local storage
      if (window.localStorage) {
        window.localStorage.setItem('ppt_locale', getters.getParticipant.locale);
      }
    }
  },
  LOAD_PAGE: async ({state, commit, getters, dispatch}, path) => {
    const response = await state.apiClient.PortalPages.getPortalPages({
      study_id: state.studyId,
      arm_id: getters.getParticipant.arm_id,
      path: `is(${path})` // use is operator to ensure empty string is sent
    })

    if (response.body.data.length > 0) {
      commit('SET_CURRENT_PAGE', response.body.data[0]);
      await dispatch('LOAD_LINKED_SURVEYS');
    } else {
      throw new Error('PAGE_DOES_NOT_EXIST');

    }
  },
  LOAD_REDIRECT_SURVEYS: async ({state, commit, getters, dispatch}, path) => {
    if (getters.isLoggedIn && getters.getParticipant) {
      const response = await state.apiClient.FeedbackEvents.getFeedbackEvents({
        study_id: getters.getParticipant.study_id,
        'participant.id': getters.getParticipant.id,
        redirect_on_dashboard: true,
        completed: 'is(null)',
        open: 'is(true)',
        'order_by': 'asc(start_date)',
      });

      // if body is empty, this map will produce an empty array
      const surveys = response.body.data.map((fe) => {
        return {
          name: fe.event.name,
          api_id: fe.api_id,
        }
      });

      // todo: once we allow skipping this should be refactored?
      commit('SET_HIDE_NAVIGATION_BUTTON', surveys.length > 0);
      commit('SET_DASHBOARD_SURVEYS', surveys);
    }
  },
  LOAD_LINKED_SURVEYS: async ({state, commit, getters}) => {
    if (!state.currentPage.surveys || state.currentPage.surveys.length == 0) {
      commit('SET_LINKED_SURVEYS', []);
      return;
    }

    // get open feedback events where the source matches linked survey sources
    const surveyIds = state.currentPage.surveys.map(s => s.id);
    const response = await state.apiClient.FeedbackEvents.getFeedbackEvents({
      study_id: getters.getParticipant.study_id,
      'participant.id': getters.getParticipant.id,
      completed: 'is(null)',
      open: 'is(true)',
      'event.source_id': `in(${surveyIds.join('|')})`
    });


    // if body is empty, this map will produce an empty array
    const surveys = response.body.data.map((fe) => {
      return {
        name: fe.event.name,
        api_id: fe.api_id,
      }
    });
    commit('SET_LINKED_SURVEYS', surveys);
  },
  LOAD_NAVIGATION_PAGES: async ({state, commit, getters}) => {
    if (getters.isLoggedIn && getters.isStartedParticipant && getters.isArmWithPortal) {
      try {
        const response = await state.apiClient.PortalPages.getPortalPages({
          'study_id': state.studyId,
          'arm_id': getters.getParticipant.arm_id,
          'show_in_nav': true,
          'order_by': 'asc(nav_order)',
        });

        if (response.body.data.length > 0) {
          commit('SET_NAVIGATION_PAGES', response.body.data);
        } else {
          throw new Error('CANT_LOAD_PORTAL_PAGES');
        }
      } catch (err) {
        throw new Error('CANT_LOAD_PORTAL_PAGES');
      }
    }
  },
  UPDATE_PROFILE: async ({state, commit, getters}, {profileFields, visibleFields}) => {
    // generate patch operation for each field that's visible
    // should this only patch fields that have changed?
    let patch = Object.keys(profileFields).filter((field) => {
      return visibleFields.includes(field);
    }).map((field) => {
      return {op: "replace", path: `/${field}`, value: profileFields[field]};
    });

    // set validation to use profile step config
    patch.push({
      op: "validationOptions",
      value: {
        "useConfig": "profilePage"
      }
    });

    try {
      await state.apiClient.Participants.updateParticipant({
        participant_id: getters.getParticipant.id,
        patch: patch
      });
    } catch (err) {
      // the above api call will throw if 400+ response
      if (err.response && err.response.body) {
        // rethrow the errors in a more friendly format
        throw {
          errors: err.response.body.errors,
          field_errors: err.response.body.field_errors
        };
      }
    }
  },
  TOGGLE_NOTIFICATIONS: async ({state, commit, dispatch}) => {
    commit('TOGGLE_NOTIFICATIONS');
    if (state.showNotifications) {
      dispatch('LOAD_NOTIFICATIONS');
    } else {
      commit('SET_NOTIFICATION_PAGE', 1);
    }
  },
  POLL_NOTIFICATIONS: async ({state, commit, getters, dispatch}) => {
    if (getters.isLoggedIn && !state.pollTimer) {
      // start the timer on login
      const pollTimer = setInterval(function () {
        dispatch('LOAD_DASHBOARD_NOTIFICATIONS')
      }, 30000);
      commit('POLL_TIMER', pollTimer);
    } else if (!getters.isLoggedIn && state.pollTimer) {
      // stop the timer on logout
      clearInterval(state.pollTimer);
      commit('POLL_TIMER', null);
    }
  },
  LOAD_NOTIFICATIONS: async ({state, commit}, nextPage = false) => {
    let currentPage = state.notificationPage;
    if (nextPage) {
      if (state.notificationPage == state.notificationTotalPages) {
        return;
      }
      currentPage += 1;
    }
    commit('LOADING_NOTIFICATIONS', true);

    try {
      const response = await state.apiClient.Notifications.getNotifications({
        'study_id': state.studyId,
        'participant.id': getters.getParticipant.id,
        'per_page': 20,
        'order_by': 'desc(created_at)',
        'page': currentPage,
      });

      if (!response.body.data) {
        commit('SET_NOTIFICATIONS', {notifications: []});
        return;
      }

      commit('SET_NOTIFICATIONS', {
        notifications: response.body.data,
        append: nextPage
      });
      commit('SET_NOTIFICATION_PAGE', currentPage);
      commit('SET_TOTAL_NOTIFICATIONS', response.body.meta.pagination.total_pages);
      commit('LOADING_NOTIFICATIONS', false);
    } catch (err) {
      commit('SET_NOTIFICATIONS', {notifications: []});
      commit('LOADING_NOTIFICATIONS', false);
    }
  },
  LOAD_DASHBOARD_NOTIFICATIONS: async ({state, commit, getters}) => {
    if (getters.isLoggedIn) {
      try {
        const response = await state.apiClient.Notifications.getNotifications({
          'study_id': state.studyId,
          'participant.id': getters.getParticipant.id,
          'sticky_dismissed': false,
          'send_email': false,
          'send_text': false,
          'send_call': false,
          'per_page': 10,
          'sticky_until': 'after(' + new Date().toLocaleString() + ')',
          'order_by': 'desc(created_at)',
          'page': 1,
        });

        if (!response.body.data) {
          commit('SET_DASHBOARD_NOTIFICATIONS', []);
          return;
        }

        commit('SET_DASHBOARD_NOTIFICATIONS', response.body.data);
      } catch (err) {
        commit('SET_DASHBOARD_NOTIFICATIONS', []);
      }
    }
  },
  DISMISS_NOTIFICATION: async ({state, commit}, notification_id) => {
    try {
      const response = await state.apiClient.Notifications.updateNotification({
        notification_id: notification_id,
        patch_content: [
          {
            "op": "replace",
            "path": "/sticky_dismissed",
            "value": true
          },
          {
            "op": "replace",
            "path": "/new",
            "value": false
          }
        ]
      });
      if (response.body.data) {
        commit('REMOVE_DASHBOARD_NOTIFICATION', response.body.data);
      }
    } catch (err) {

    }
  },
  GET_SURVEY_EVENT: async ({state}, api_id) => {
    const response = await state.apiClient.SurveyEvents.getSurveyEvent({api_id});
    if (response.body.data) {
      return response.body.data;
    } else {
      throw new Error('SURVEY_DOES_NOT_EXIST');
    }
  },
  SAVE_PARTIAL_SURVEY: async ({state}, {api_id, data}) => {
    try {
      await state.apiClient.SurveyEvents.updateSurveyEvent({
        'api_id': api_id,
        'patch_content': [
          {
            op: 'replace',
            path: '/partial_data',
            value: data
          }
        ]
      });
    } catch (err) {
      ApiErrorHandler(err);
    }
  },
  SUBMIT_SURVEY: async ({state}, {api_id, body}) => {
    try {
      await state.apiClient.SurveyEvents.createSurveyFormSubmission({
        'api_id': api_id,
        'submission': {
          'fields': body
        }
      });
    } catch (err) {
      if (err.response.body.errors) {
        throw {
          status: err.status,
          message: err.response.body.errors[0]
        };
      } else {
        throw err;
      }
    }
  },
  LOGIN_LINK_REQUEST: async({state}, {account_identifier}) => {
    try {
      await state.apiClient.Authentication.requestLoginLink({
        'content': {
          'account_identifier': account_identifier
        }
      });
    } catch (err) {
      if (err.response.body.errors) {
        throw {
          status: err.status,
          message: err.response.body.errors[0]
        };
      } else {
        throw err;
      }
    }
  }
}

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  strict: process.env.NODE_ENV !== 'production'
});
